"use client"

import React, { useState, useEffect } from 'react';
import { Spinner } from '@t-one-internal/design-system';
import { ChartBar, ArrowsClockwise, Warning } from '@phosphor-icons/react';
import { analyticsService } from '../../../services/analyticsService';
import type { EmployeeDetails } from '../../../stores/employeeProfileStore';
import {
  MultiKPI,
  GoalList,
  ReviewCycleCard,
  CheckInCycleCard,
  FeedbackCard,
  StatusSummary,
  RankedList,
  PendingTasksCard,
} from '@t-one-internal/design-system//lib/components/widgets/my-portal/';

interface MetricConfig {
  key: string;
  title: string;
  metricKey: string;
  component: string;
  visualizationConfig?: Record<string, unknown>;
  colSpan?: 1 | 2;
}

const EMPLOYEE_METRICS: MetricConfig[] = [
  {
    key: 'participation_summary',
    title: 'Participation Summary',
    metricKey: 'user_participation_summary',
    component: 'multi_kpi',
    colSpan: 2,
    visualizationConfig: {
      kpis: [
        { key: 'cyclesCount', label: 'Active Cycles', icon: 'cycle' },
        { key: 'goalsCount', label: 'Goals', icon: 'target' },
        { key: 'pipsCount', label: 'Active PIPs', icon: 'warning' },
      ],
    },
  },
  {
    key: 'goal_status',
    title: 'Goal Status Summary',
    metricKey: 'user_goal_status_summary',
    component: 'status_summary',
    visualizationConfig: { statusField: 'status', countField: 'value' },
  },
  {
    key: 'goals_list',
    title: 'Goals',
    metricKey: 'user_goals_list',
    component: 'goal_list',
    colSpan: 2,
  },
  {
    key: 'review_status',
    title: 'Review Status Summary',
    metricKey: 'user_review_status_summary',
    component: 'status_summary',
    visualizationConfig: { statusField: 'flow_status', countField: 'value' },
  },
  {
    key: 'review_cycles',
    title: 'Review Cycles History',
    metricKey: 'user_review_cycles_history',
    component: 'review_cycle_card',
    colSpan: 2,
  },
  {
    key: 'checkins',
    title: 'Check-ins (1:1s)',
    metricKey: 'user_checkin_summary',
    component: 'checkin_card',
    colSpan: 2,
  },
  {
    key: 'top_competencies',
    title: 'Top Competencies',
    metricKey: 'user_top_competencies',
    component: 'ranked_list',
    visualizationConfig: { labelField: 'competency_title', valueField: 'value', maxItems: 5, sortDirection: 'desc' },
    colSpan: 2,
  },
  {
    key: 'improvement_areas',
    title: 'Areas for Improvement',
    metricKey: 'user_improvement_competencies',
    component: 'ranked_list',
    visualizationConfig: { labelField: 'competency_title', valueField: 'value', maxItems: 5, sortDirection: 'asc' },
    colSpan: 2,
  },
  {
    key: '360_as_reviewer',
    title: '360 Feedback Given',
    metricKey: 'user_360_feedback_as_reviewer',
    component: 'feedback_card',
    visualizationConfig: { role: 'reviewer' },
  },
  {
    key: '360_as_reviewee',
    title: '360 Feedback Received',
    metricKey: 'user_360_feedback_as_reviewee',
    component: 'feedback_card',
    visualizationConfig: { role: 'reviewee' },
  },
];

export interface SectionConfig {
  id: string;
  title: string;
  metricKeys: string[];
}

export const SECTIONS: SectionConfig[] = [
  { id: 'participation', title: 'Participation Summary', metricKeys: ['participation_summary'] },
  { id: 'goals', title: 'Goals', metricKeys: ['goals_list'] },
  { id: 'review_cycles', title: 'Review Cycles History', metricKeys: ['review_cycles'] },
  { id: 'checkins', title: 'Check-ins (1:1s)', metricKeys: ['checkins'] },
  { id: 'competencies', title: 'Top Competencies', metricKeys: ['top_competencies'] },
  { id: 'improvement', title: 'Areas for Improvement', metricKeys: ['improvement_areas'] },
  { id: 'feedback', title: '360 Feedback', metricKeys: ['360_as_reviewer', '360_as_reviewee'] },
];

interface WidgetCardProps {
  title: string;
  loading: boolean;
  error: string | null;
  children: React.ReactNode;
  onRetry?: () => void;
  colSpan?: 1 | 2;
}

const WidgetCard: React.FC<WidgetCardProps> = ({ title, loading, error, children, onRetry, colSpan = 1 }) => {
  const showHeader = title.includes('360 Feedback');
  return (
    <div className={`bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden ${colSpan === 2 ? 'md:col-span-2' : ''}`}>
      {(showHeader || (error && onRetry)) && (
        <div className={`px-4 py-3 flex items-center justify-between ${showHeader ? 'border-b border-gray-100' : ''}`}>
          {showHeader ? <h4 className="text-sm font-medium text-gray-900">{title}</h4> : <div />}
          {error && onRetry && (
            <button onClick={onRetry} className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors" title="Retry">
              <ArrowsClockwise size={16} />
            </button>
          )}
        </div>
      )}
      <div className="p-4 min-h-[120px]">
        {loading ? (
          <div className="flex items-center justify-center h-full py-8">
            <Spinner size="sm" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full py-8 text-gray-400">
            <Warning size={24} className="mb-2 text-amber-400" />
            <span className="text-sm text-center">{error}</span>
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
};

const RawDataTable: React.FC<{ data: any[]; columns?: string[] }> = ({ data, columns }) => {
  if (!data || data.length === 0) {
    return <div className="text-sm text-gray-500 text-center py-4">No data available</div>;
  }
  const displayColumns = columns || Object.keys(data[0] || {});
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            {displayColumns.map((col) => (
              <th key={col} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{col.replace(/_/g, ' ')}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.slice(0, 10).map((row, idx) => (
            <tr key={idx} className="border-b border-gray-100 last:border-0">
              {displayColumns.map((col) => (
                <td key={col} className="px-3 py-2 text-gray-700">
                  {typeof row[col] === 'number' ? row[col].toFixed(2) : row[col] ?? '-'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {data.length > 10 && (
        <div className="text-xs text-gray-400 text-center py-2">Showing 10 of {data.length} rows</div>
      )}
    </div>
  );
};

interface AnalyticsSectionProps {
  employee: EmployeeDetails;
  sectionId?: string;
  hideTitle?: boolean;
}

export const AnalyticsSection: React.FC<AnalyticsSectionProps> = ({ employee, sectionId = 'participation', hideTitle = false }) => {
  const [analyticsData, setAnalyticsData] = useState<Record<string, any>>({});
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});
  const [errorStates, setErrorStates] = useState<Record<string, string | null>>({});
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const section = SECTIONS.find(s => s.id === sectionId);
  const activeMetrics = section
    ? EMPLOYEE_METRICS.filter(m => section.metricKeys.includes(m.key))
    : [];

  const employeeUserId = (() => {
    if (employee.user_uuid) return String(employee.user_uuid);
    if (employee.id) return String(employee.id);
    return undefined;
  })();

  const fetchMetric = async (metricKey: string) => {
    setLoadingStates((prev) => ({ ...prev, [metricKey]: true }));
    setErrorStates((prev) => ({ ...prev, [metricKey]: null }));

    try {
      const response = await analyticsService.fetchWidgetData(
        { metric_key: metricKey },
        { ownerUserId: employeeUserId }
      );
      setAnalyticsData((prev) => ({ ...prev, [metricKey]: response.data }));
    } catch (err: any) {
      console.error(`Failed to fetch metric ${metricKey}:`, err);
      setErrorStates((prev) => ({ ...prev, [metricKey]: err.error || err.details || 'Failed to load data' }));
    } finally {
      setLoadingStates((prev) => ({ ...prev, [metricKey]: false }));
    }
  };

  useEffect(() => {
    if (!employeeUserId) return;

    const fetchAllMetrics = async () => {
      const initialLoading: Record<string, boolean> = {};
      activeMetrics.forEach((metric) => { initialLoading[metric.metricKey] = true; });
      setLoadingStates(initialLoading);
      await Promise.all(activeMetrics.map((metric) => fetchMetric(metric.metricKey)));
      setIsInitialLoad(false);
    };

    fetchAllMetrics();
  }, [employeeUserId, sectionId]);

  const renderWidgetContent = (metric: MetricConfig) => {
    const data = analyticsData[metric.metricKey];
    if (!data || (Array.isArray(data) && data.length === 0)) {
      return <div className="text-sm text-gray-500 text-center py-4">No data available</div>;
    }

    switch (metric.component) {
      case 'multi_kpi':
        return <MultiKPI data={data} options={metric.visualizationConfig} kpis={(metric.visualizationConfig as any)?.kpis} />;
      case 'goal_list':
        return <GoalList data={data} options={metric.visualizationConfig} />;
      case 'review_cycle_card':
        return <ReviewCycleCard data={data} options={metric.visualizationConfig} />;
      case 'checkin_card':
        return <CheckInCycleCard data={data} options={metric.visualizationConfig} />;
      case 'feedback_card':
        return <FeedbackCard data={data} options={metric.visualizationConfig} />;
      case 'status_summary':
        return <StatusSummary data={data} options={metric.visualizationConfig} />;
      case 'ranked_list':
        return <RankedList data={data} options={metric.visualizationConfig} />;
      case 'pending_tasks':
        return <PendingTasksCard data={data} options={metric.visualizationConfig} />;
      case 'raw_data':
        return <RawDataTable data={data} columns={(metric.visualizationConfig as any)?.columns} />;
      default:
        return <pre className="text-xs text-gray-500 overflow-auto">{JSON.stringify(data, null, 2)}</pre>;
    }
  };

  if (!employeeUserId) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400">
        <span>No employee selected</span>
      </div>
    );
  }

  return (
    <div className="w-full">
      {!hideTitle && (
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-slate-900">{section?.title || 'Analytics'}</h3>
          <button
            onClick={() => activeMetrics.forEach((m) => fetchMetric(m.metricKey))}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            disabled={Object.values(loadingStates).some(Boolean)}
          >
            <ArrowsClockwise size={14} className={Object.values(loadingStates).some(Boolean) ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      )}

      {isInitialLoad && Object.values(loadingStates).every(Boolean) ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-3">
            <Spinner size="lg" />
            <span className="text-sm text-gray-500">Loading analytics...</span>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {activeMetrics.map((metric) => (
            <WidgetCard
              key={metric.key}
              title={metric.title}
              loading={loadingStates[metric.metricKey] || false}
              error={errorStates[metric.metricKey] || null}
              onRetry={() => fetchMetric(metric.metricKey)}
              colSpan={metric.colSpan}
            >
              {renderWidgetContent(metric)}
            </WidgetCard>
          ))}
        </div>
      )}

      {!isInitialLoad &&
        !Object.values(loadingStates).some(Boolean) &&
        Object.values(errorStates).every((e) => e !== null) && (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <ChartBar size={48} className="mb-3 text-gray-300" />
            <span className="text-sm">No analytics data available for this employee</span>
          </div>
        )}
    </div>
  );
};

export default AnalyticsSection;
